import { forwardRef, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Input, type InputProps } from './Input'

// PasswordInput — field base + show/hide toggle + optional strength meter +
// optional live rules checklist (design/components/password.html). Strength
// and rules are honest guidance that must mirror the real backend policy.
// Hidden by default; the toggle is a real button (aria-label + aria-pressed).

const STRENGTH = {
  weak: { fill: 1, bar: 'bg-sens', text: 'text-sens', labelKey: 'ui.password.weak' },
  mid: { fill: 2, bar: 'bg-warn', text: 'text-warn', labelKey: 'ui.password.mid' },
  strong: { fill: 3, bar: 'bg-ok', text: 'text-ok', labelKey: 'ui.password.strong' },
} as const

export interface PasswordInputProps extends InputProps {
  /** Show the eye toggle. Default true. */
  showToggle?: boolean
  /** Strength level; undefined hides the meter. Always reinforced with text. */
  strength?: keyof typeof STRENGTH
  /** Live policy checklist; undefined hides it. Must match backend rules. */
  rules?: { label: string; met: boolean }[]
}

/** Input with show/hide toggle (real button, aria-pressed), optional strength
 *  meter and live rules checklist wired into aria-describedby. Strength/rules
 *  are display-only — the caller computes them and they must mirror the real
 *  backend password policy. */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { showToggle = true, strength, rules, className, 'aria-describedby': describedBy, ...rest },
    ref,
  ) {
    const { t } = useTranslation('components')
    const [visible, setVisible] = useState(false)
    const rid = useId()
    const rulesId = rules ? `${rid}-rules` : undefined
    const strengthId = strength ? `${rid}-strength` : undefined
    const describedByAll = [describedBy, strengthId, rulesId].filter(Boolean).join(' ') || undefined
    const s = strength ? STRENGTH[strength] : null

    return (
      <div className="flex w-full flex-col">
        <span className="relative flex w-full items-center">
          <Input
            ref={ref}
            type={visible ? 'text' : 'password'}
            aria-describedby={describedByAll}
            className={cn(showToggle && 'pr-9', className)}
            {...rest}
          />
          {showToggle && (
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              aria-pressed={visible}
              aria-label={visible ? t('ui.password.hide') : t('ui.password.show')}
              className="absolute right-[7px] inline-flex size-7 items-center justify-center rounded-[6px] text-text-3 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue focus-visible:outline-offset-1 [&_svg]:size-[15px]"
            >
              {visible ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="m2 2 20 20" />
                  <path d="M6.7 6.7C4.1 8.3 2 12 2 12s3.5 7 10 7c2 0 3.7-.5 5.2-1.3" />
                  <path d="M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 7 10 7a18 18 0 0 1-2.3 3.2" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          )}
        </span>

        {s && (
          <>
            <div className="mt-[7px] flex gap-1" aria-hidden>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-[2px] transition-colors',
                    i < s.fill ? s.bar : 'bg-field',
                  )}
                />
              ))}
            </div>
            <span
              id={strengthId}
              aria-live="polite"
              className={cn('mt-[5px] text-[10px] font-semibold', s.text)}
            >
              {t('ui.password.strength')}: {t(s.labelKey)}
            </span>
          </>
        )}

        {rules && (
          <ul id={rulesId} aria-live="polite" className="mt-[9px] flex flex-col gap-1.5">
            {rules.map((r, i) => (
              <li
                key={i}
                className={cn(
                  'flex items-center gap-[7px] text-[11px]',
                  r.met ? 'text-ok' : 'text-text-3',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-[14px] flex-none items-center justify-center rounded-full border-[1.5px]',
                    r.met ? 'border-ok bg-ok text-white' : 'border-input-line text-transparent',
                  )}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    className="size-[9px]"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span>{r.label}</span>
                <span className="sr-only">
                  {r.met ? t('ui.password.ruleMet') : t('ui.password.rulePending')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'
